import { NavLink } from "react-router-dom";
import {
  Sparkles,
  Palette,
  LayoutGrid,
  FolderOpen,
  ListChecks,
  Diamond,
  ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/components/marketing/BrandMark";
import { useAuthStore } from "@/stores/auth";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const PRIMARY: NavItem[] = [
  { to: "/free", label: "自由创作", icon: Sparkles },
  { to: "/styles", label: "风格广场", icon: Palette },
];

const ASSETS: NavItem[] = [
  { to: "/works", label: "我的作品", icon: LayoutGrid },
  { to: "/assets", label: "素材库", icon: FolderOpen },
  { to: "/jobs", label: "任务中心", icon: ListChecks },
];

const ACCOUNT: NavItem[] = [
  { to: "/billing", label: "点数流水", icon: Diamond },
];

function NavGroup({ items }: { items: NavItem[] }) {
  return (
    <div className="flex flex-col gap-0.5">
      {items.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          className={({ isActive }) =>
            cn(
              "flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm font-medium transition-colors",
              isActive
                ? "bg-card text-foreground shadow-[inset_0_0_0_1px_hsl(var(--border))]"
                : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground/85"
            )
          }
        >
          {({ isActive }) => (
            <>
              <Icon
                className={cn(
                  "h-4 w-4 shrink-0 transition-colors",
                  isActive ? "text-accent" : "opacity-85"
                )}
              />
              <span>{label}</span>
            </>
          )}
        </NavLink>
      ))}
    </div>
  );
}

export function Sidebar({
  assetCount,
  jobCount,
}: {
  assetCount: number;
  jobCount: number;
}) {
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  const accountItems: NavItem[] = isAdmin
    ? [...ACCOUNT, { to: "/admin", label: "后台", icon: ShieldCheck }]
    : ACCOUNT;

  return (
    <aside className="flex h-screen flex-col gap-2 overflow-y-auto border-r border-border bg-secondary px-3 py-5">
      <div className="mb-4 px-3 py-2">
        <BrandMark size="lg" />
      </div>

      <nav aria-label="主导航" className="flex flex-col gap-2">
        <NavGroup items={PRIMARY} />
        <div className="mx-3 my-3 h-px bg-border" />
        <NavGroup items={ASSETS} />
        <div className="mx-3 my-3 h-px bg-border" />
        <NavGroup items={accountItems} />
      </nav>

      <div className="mt-auto flex flex-col gap-1 border-t border-border p-3 pt-3">
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>素材</span>
          <strong className="font-semibold text-foreground">{assetCount}</strong>
        </div>
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>任务</span>
          <strong className="font-semibold text-foreground">{jobCount}</strong>
        </div>
      </div>
    </aside>
  );
}
